import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faGrid, faList, faHome} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box, Button, Columns, DashboardModule, Grid, LoadingModule, PageHeader,
  Tools,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'
import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PageContext from '@contexts/pageContext'
import type { DefaultContext } from '@contexts/pageContext'

import useIntegration from '@hooks/useIntegration'
import { generateUrls } from '@hooks/useTemplatePage'
import useTemplatePages from '@hooks/useTemplatePages'

import type { TemplatePageModel } from '@models/types'

import PageFilterModule from '@components/PageFilterModule'
import TemplatePageGridTile from '@components/TemplatePageGridTile/TemplatePageGridTile'

import EditTemplateModal from '@modals/EditTemplatePageModal/EditTemplatePageModal'

import IntegrationBanner from './IntegrationBanner'
import CanvaTemplatePageBlankState from './CanvaTemplatePageBlankState'

import type { PageContextPayload } from '../List'
import ListView from './ListView'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ brand_id }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'templateGroups',
        label: 'Template Group',
      },
      key: 'template_group_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brandThemes',
        label: 'Brand Theme',
      },
      key: 'brand_theme_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ brand_id }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
        label: 'Agency',
      },
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'services',
        label: 'Service',
      },
      key: 'service_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ service_id }) => ({ service_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'serviceProducts',
        label: 'Product',
      },
      key: 'service_product_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
        label: 'Publication',
      },
      key: 'publication_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useLocation()

  const { integrated, integratedBy } = useIntegration()

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden?.toString() ?? 'false'

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...pageFilters,
      hidden: isHidden === 'true',
    },
    performHttpRequests: integrated,
    requestOptions: {
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
    },
  })

  const {
    callbacks: {
      loadMore,
      syncBrandTemplates,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { canvaTemplatePagesIndexUrl } = generateUrls()

  const [selectedTemplatePage, setSelectedTemplatePage] = useState<null | TemplatePageModel>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')

  return (
    <>
      <Helmet>
        <title>Canva Templates | Template Manager</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            disabled={!integrated}
            size="medium"
            onClick={() => { syncBrandTemplates() }}
          >
            Link New Templates
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: canvaTemplatePagesIndexUrl,
            icon: faHome,
            key: 'false',
            title: 'Linked Templates',
          },
          // {
          //   href: canvaTemplatesUrl,
          //   icon: faHome,
          //   key: 'true',
          //   title: 'Field Mapping',
          // },
          // {
          //   href: canvaTemplatesUrl,
          //   icon: faHistory,
          //   key: 'true',
          //   title: 'Activity',
          // },
        ]}
        title="Canva Templates"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            <IntegrationBanner integrated={integrated} integratedBy={integratedBy} />

            { !loading && !hasFilteredTemplatePages && (
              <CanvaTemplatePageBlankState
                integrated={integrated}
                hasFilteredTemplatePages={hasFilteredTemplatePages}
              />
            )}

            {integrated && !loading && hasFilteredTemplatePages && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Canva Templates">
                <Box flexDirection="column">
                  {viewMode === 'grid' && (
                    <Grid
                      // @ts-expect-error TS2322
                      gridGap="medium"
                      gridAutoRows="auto"
                      gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                      padding="large"
                    >
                      {filteredTemplatePages.map((templatePage: TemplatePageModel) => (
                        <TemplatePageGridTile
                          key={templatePage.id}
                          templatePage={templatePage}
                          setSelectedTemplatePage={setSelectedTemplatePage}
                        />
                      ))}
                    </Grid>
                  )}

                  {viewMode === 'list' && (
                    <ListView setSelectedTemplatePage={setSelectedTemplatePage} templatePages={filteredTemplatePages}/>
                  )}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          { integrated && (
          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={viewMode === 'list' ? faGrid : faList} />}
                onClick={viewMode === 'list' ? () => setViewMode('grid') : () => setViewMode('list')}
                size="medium"
              >
                {viewMode === 'list' ? 'View as Grid' : 'View as List'}
              </Button>
            </Tools>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
          )}

           {integrated && selectedTemplatePage !== null && (
            <EditTemplateModal
              showModal
              modalKey="EditTemplateModal"
              templatePage={selectedTemplatePage}
              callbacks={{
                closeModal: () => { setSelectedTemplatePage(null) },
                editTemplatePage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
              }}
            />
          )}
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
